import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import PageForm, { FormField } from '../../../../commons/PageForm';
import { AlertVariant } from '../../../../commons/PageAlert';
import {
  defaultPoliticaMailForm,
  dtoToPoliticaMailForm,
  emptyPoliticaMailDto,
  hasValidMailTemplate,
  isPoliticaMailConfigured,
  POLITICA_MAIL_PLACEHOLDERS,
  PoliticaMail,
  PoliticaMailAccion,
  PoliticaMailForm,
  politicaMailFormToDto,
  validatePoliticaMailForm,
} from '../../../../shared/politica-mail';
import type { CategoriaActividadDTO } from '../../../../shared/categoria-actividad';
import Confirm, { useConfirm } from '../../../../commons/Confirm';

function extractApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail ?? err.response?.data?.message;
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  return 'No se pudo completar la operación.';
}

interface CategoriaActividadMailTabProps {
  categoriaActividadId: string;
  onBack: () => void;
  onNotify: (variant: AlertVariant, message: string) => void;
}

const emptyAccion = (): PoliticaMailAccion => ({
  nombre: '',
  descripcion: '',
  estado: 'PENDIENTE',
});

export default function CategoriaActividadMailTab({
  categoriaActividadId,
  onBack,
  onNotify,
}: CategoriaActividadMailTabProps) {
  const [form, setForm] = useState<PoliticaMailForm>(defaultPoliticaMailForm());
  const [errors, setErrors] = useState<Partial<Record<keyof PoliticaMailForm, string>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [audit, setAudit] = useState<{ updated?: string }>({});
  const [hasPolitica, setHasPolitica] = useState(false);
  const [entity, setEntity] = useState<CategoriaActividadDTO | null>(null);

  const { confirmConfig, ask, dismiss } = useConfirm();

  const loadPoliticaMail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<CategoriaActividadDTO>(
        `api/categoria-actividads/${categoriaActividadId}`,
      );
      setEntity(res.data);
      setForm(dtoToPoliticaMailForm(res.data.politicaMail));
      setHasPolitica(isPoliticaMailConfigured(res.data.politicaMail));
      setAudit({ updated: res.data.updated });
      setLoaded(true);
    } catch (err) {
      onNotify('error', extractApiError(err));
      setLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [categoriaActividadId, onNotify]);

  useEffect(() => {
    loadPoliticaMail();
  }, [loadPoliticaMail]);

  const handleFieldChange = (name: keyof PoliticaMailForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleAccionChange = (index: number, field: keyof PoliticaMailAccion, value: string) => {
    setForm(prev => ({
      ...prev,
      acciones: prev.acciones.map((accion, idx) => (
        idx === index ? { ...accion, [field]: value } : accion
      )),
    }));
    setErrors(prev => ({ ...prev, acciones: undefined }));
  };

  const handleAddAccion = () => {
    setForm(prev => ({ ...prev, acciones: [...prev.acciones, emptyAccion()] }));
  };

  const handleRemoveAccion = (index: number) => {
    setForm(prev => ({
      ...prev,
      acciones: prev.acciones.filter((_, idx) => idx !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entity) {
      onNotify('error', 'No se pudo cargar la categoría.');
      return;
    }
    const validationErrors = validatePoliticaMailForm(form);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const payload: CategoriaActividadDTO = {
        ...entity,
        id: Number(categoriaActividadId),
        politicaMail: politicaMailFormToDto(form),
      };
      const res = await axios.put<CategoriaActividadDTO>(
        `api/categoria-actividads/${categoriaActividadId}`,
        payload,
      );
      setEntity(res.data);
      setForm(dtoToPoliticaMailForm(res.data.politicaMail));
      setHasPolitica(isPoliticaMailConfigured(res.data.politicaMail));
      setAudit({ updated: res.data.updated });
      onNotify('success', 'Política de mail guardada correctamente.');
    } catch (err) {
      onNotify('error', extractApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!entity) {
      onNotify('error', 'No se pudo cargar la categoría.');
      return;
    }
    ask(
      <>¿Confirmas que deseas eliminar la política de mail de esta categoría? Las actividades nuevas no generarán mail.</>,
      async () => {
        setDeleting(true);
        try {
          const payload: CategoriaActividadDTO = {
            ...entity,
            id: Number(categoriaActividadId),
            politicaMail: emptyPoliticaMailDto(),
          };
          const res = await axios.put<CategoriaActividadDTO>(
            `api/categoria-actividads/${categoriaActividadId}`,
            payload,
          );
          dismiss();
          setEntity(res.data);
          setForm(defaultPoliticaMailForm());
          setHasPolitica(false);
          setAudit({ updated: res.data.updated });
          onNotify('success', 'Política de mail eliminada correctamente.');
        } catch (err) {
          onNotify('error', extractApiError(err));
        } finally {
          setDeleting(false);
        }
      },
      { title: 'Eliminar política de mail', confirmLabel: 'Eliminar', variant: 'danger' },
    );
  };

  const fields: FormField<PoliticaMailForm>[] = useMemo(() => [
    {
      name: 'requeridoParaTerminar',
      label: 'Requerido para terminar',
      type: 'checkbox',
      hint: 'Si está activo, el usuario debe enviar el mail para poder terminar la actividad',
    },
    {
      name: 'template',
      label: 'Plantilla del correo',
      type: 'textarea',
      rows: 10,
      fullWidth: true,
      placeholder: 'Estimado cliente,\n\n{{asunto}}\n\nAtentamente,\n{{usuario}}',
      hint: `Placeholders: ${POLITICA_MAIL_PLACEHOLDERS.join(', ')}. Obligatoria para generar mail: sin texto (vacío o solo espacios) no se crea ActividadMail al asignar actividades.`,
    },
  ], []);

  const templateGeneratesMail = hasValidMailTemplate(form.template);

  if (loading) {
    return <div style={s.loadingBox}>Cargando política de mail…</div>;
  }

  if (!loaded) {
    return <div style={s.loadingBox}>No se pudo cargar la política de mail.</div>;
  }

  return (
    <>
      <Confirm config={confirmConfig} />

      {audit.updated && (
        <div style={s.auditBox}>
          Última modificación de categoría: {new Date(audit.updated).toLocaleString()}
        </div>
      )}

      {!templateGeneratesMail && (
        <div style={s.infoBox}>
          Sin plantilla con texto no se generará mail en actividades nuevas (politicaMail nulo o template en blanco).
        </div>
      )}

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={false}
        saving={saving}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={onBack}
        accentColor="#f59e0b"
        submitLabel="Guardar política de mail"
        actionSlotAfterCancel={hasPolitica ? (
          <button
            type="button"
            style={s.deleteBtn}
            onClick={handleDelete}
            disabled={deleting || saving}
          >
            <FaTrash style={{ marginRight: 6 }} />
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </button>
        ) : undefined}
        contentBeforeActions={(
          <div style={s.accionesSection}>
            <div style={s.accionesHeader}>
              <div>
                <div style={s.accionesTitle}>Acciones</div>
                <div style={s.accionesHint}>Acciones disponibles en la actividad de mail con su estado inicial.</div>
              </div>
              <button type="button" style={s.addBtn} onClick={handleAddAccion}>
                <FaPlus style={{ marginRight: 6 }} />
                Agregar acción
              </button>
            </div>

            {errors.acciones && (
              <div style={s.errorBox}>{errors.acciones}</div>
            )}

            {form.acciones.length === 0 ? (
              <div style={s.emptyAcciones}>Sin acciones configuradas.</div>
            ) : (
              <div style={s.accionesList}>
                {form.acciones.map((accion, index) => (
                  <div key={index} style={s.accionRow}>
                    <input
                      className="profase-form-input"
                      value={accion.nombre ?? ''}
                      placeholder="Nombre (ej. enviar)"
                      onChange={e => handleAccionChange(index, 'nombre', e.target.value)}
                    />
                    <input
                      className="profase-form-input"
                      value={accion.descripcion ?? ''}
                      placeholder="Descripción"
                      onChange={e => handleAccionChange(index, 'descripcion', e.target.value)}
                    />
                    <input
                      className="profase-form-input"
                      value={accion.estado ?? ''}
                      placeholder="Estado (ej. PENDIENTE)"
                      onChange={e => handleAccionChange(index, 'estado', e.target.value)}
                    />
                    <button
                      type="button"
                      style={s.removeBtn}
                      onClick={() => handleRemoveAccion(index)}
                      title="Quitar acción"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      />
    </>
  );
}

const s: Record<string, React.CSSProperties> = {
  loadingBox: {
    padding: '32px 24px',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    color: '#64748b',
    fontSize: 14,
    textAlign: 'center',
  },
  auditBox: {
    padding: '8px 12px',
    marginBottom: 12,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    color: '#64748b',
    fontSize: 12,
  },
  infoBox: {
    padding: '10px 12px',
    marginBottom: 12,
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: 8,
    color: '#92400e',
    fontSize: 12,
  },
  deleteBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 14px',
    border: '1px solid #fca5a5',
    borderRadius: 8,
    background: '#fff1f2',
    color: '#dc2626',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  accionesSection: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: '16px 0 4px',
    borderTop: '1px solid #e2e8f0',
  },
  accionesHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  accionesTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#334155',
  },
  accionesHint: {
    marginTop: 4,
    fontSize: 12,
    color: '#64748b',
  },
  addBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    background: '#fff',
    color: '#334155',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  accionesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  accionRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.4fr 1fr auto',
    gap: 8,
    alignItems: 'center',
  },
  removeBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    border: '1px solid #fca5a5',
    borderRadius: 8,
    background: '#fff1f2',
    color: '#dc2626',
    cursor: 'pointer',
  },
  emptyAcciones: {
    padding: '12px 14px',
    border: '1px dashed #cbd5e1',
    borderRadius: 8,
    color: '#64748b',
    fontSize: 13,
  },
  errorBox: {
    padding: '8px 12px',
    borderRadius: 8,
    background: '#fff1f2',
    border: '1px solid #fca5a5',
    color: '#dc2626',
    fontSize: 12,
  },
};
